import api from '@/services/api';
import type { DocumentType, AssignableDocumentType } from '@/features/document-templates/types/documentTemplates.types';
import type {
    GeneratedDocument,
    GeneratedDocumentsPage,
    GeneratedDocumentStatus,
    GenerationFailureReason,
} from '../types/agreementDocuments.types';

export const DOCUMENTS_PER_PAGE = 20;

type RawNode = Record<string, unknown>;

const isRecord = (value: unknown): value is RawNode => typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asInt = (value: unknown, min: number, fallback: number): number => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

const mapRequestedBy = (raw: unknown): GeneratedDocument['requestedBy'] =>
    isRecord(raw) ? { id: String(raw.id ?? ''), name: asString(raw.name) } : null;

const mapTemplate = (raw: unknown): GeneratedDocument['template'] =>
    isRecord(raw)
        ? {
            id: String(raw.id ?? ''),
            name: String(raw.name ?? ''),
            versionId: String(raw.version_id ?? ''),
            versionNumber: Number(raw.version_number ?? 0),
        }
        : null;

const mapFailure = (raw: unknown): GeneratedDocument['failure'] =>
    isRecord(raw) ? { reason: String(raw.reason ?? '') as GenerationFailureReason, detail: asString(raw.detail) } : null;

export const mapGeneratedDocument = (raw: RawNode): GeneratedDocument => ({
    id: String(raw.id ?? ''),
    agreementId: String(raw.agreement_id ?? ''),
    documentType: String(raw.document_type ?? '') as DocumentType,
    status: String(raw.status ?? '') as GeneratedDocumentStatus,
    trigger: String(raw.trigger ?? ''),
    requestedBy: mapRequestedBy(raw.requested_by),
    template: mapTemplate(raw.template),
    checksumSha256: asString(raw.checksum_sha256),
    byteSize: typeof raw.byte_size === 'number' ? raw.byte_size : null,
    emptyVariables: Array.isArray(raw.empty_variables) ? raw.empty_variables.map(String) : [],
    attempts: Number(raw.attempts ?? 0),
    failure: mapFailure(raw.failure),
    generatedAt: asString(raw.generated_at),
    failedAt: asString(raw.failed_at),
    createdAt: String(raw.created_at ?? ''),
});

const collectionPath = (agreementId: string): string => `/documents/agreements/${agreementId}/documents`;

/** Order is the server's: newest first. */
export const fetchAgreementDocuments = async (
    agreementId: string,
    params: { page: number; perPage: number },
): Promise<GeneratedDocumentsPage> => {
    const raw = (
        await api.get(collectionPath(agreementId), { params: { page: params.page, per_page: params.perPage } })
    ).data;
    const meta = isRecord(raw.meta) ? raw.meta : {};

    return {
        data: ((raw.data ?? []) as RawNode[]).map(mapGeneratedDocument),
        meta: {
            currentPage: asInt(meta.current_page, 1, params.page),
            lastPage: asInt(meta.last_page, 1, 1),
            perPage: asInt(meta.per_page, 1, params.perPage),
            total: asInt(meta.total, 0, 0),
        },
    };
};

export const generateAgreementDocument = async (
    agreementId: string,
    documentType: AssignableDocumentType,
): Promise<GeneratedDocument> =>
    mapGeneratedDocument((await api.post(collectionPath(agreementId), { document_type: documentType })).data.data);

export type FileDisposition = 'inline' | 'attachment';

export const fetchGeneratedDocumentFile = async (documentId: string, disposition: FileDisposition): Promise<Blob> => {
    const response = await api.get<Blob>(`/documents/generated-documents/${documentId}/file`, {
        params: { disposition },
        responseType: 'blob',
        suppressGlobalForbiddenToast: true,
    });

    return new Blob([response.data], { type: 'application/pdf' });
};
